import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, MenuItem, FormControl, InputLabel, Select, Box, Typography, InputAdornment, Tooltip, Modal, IconButton, ListSubheader, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { question as questionConstant } from './constants';
import Button from '../../components/Button';
import TextField from '../../components/TextField';
import { useTheme } from '@mui/material/styles';
import { useQuestionBank } from './hooks';
import Layout from './Layout';
import { CaretDown, Info, X } from '@phosphor-icons/react';
import LayoutMcqMsq from './LayoutMcqMsq';
import LayoutFill from './LayoutFill';
import LayoutMtf from './LayoutMtf';
import PreviewQuestion from './PreviewQuestion';
import Preview from '../course/lessonPlan/assessments/PreviewQuestion'
import Loader from '../../components/Loader';
import { useCourse } from '../course/hooks';
import { course as courseConst } from '../course/constants';


export const Edit = ({ courseId = null, sectionId = null, chapterId = null, planId = null, manageQuestion = false, questionId = null, setQuestionId, setIsSave = false, onQuestionSaved = {} }) => {
    const theme = useTheme();
    const { id } = useParams();
    const navigate = useNavigate();
    const { current, loadCurrent, loading, save, create, groupList, loadGroup, loadLessonPlan, lessonPlan, loadQuestions, loadCourseQuestions } = useQuestionBank();
    const { loadCourses, loadCoursesList, courseList, diagnosticList, allCourseList, resetCoursesList, lastPage, courses } = useCourse();
    const [category, setCategory] = useState();
    const [sectionList, setSectionList] = useState();
    const [chapterList, setChapterList] = useState();
    const [question, setQuestion] = useState({});
    const [questionDetails, setQuestionDetails] = useState();
    const [groupDetails, setGroupDetails] = useState();
    const [values, setValues] = useState([]);
    const [mtfLhs, setMtfLhs] = useState([]);
    const [mtfRhs, setMtfRhs] = useState([]);
    const [optionDetails, setOptionDetails] = useState({});
    const [answerKey, setAnswerKey] = useState({});
    const [helpText, setHelpText] = useState({});
    const [answerLabels, setAnswerLabels] = useState([]);
    const [marks, setMarks] = useState();
    const [openPreview, setOpenPreview] = useState(false);
    const [page, setPage] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const [marksManuallySet, setMarksManuallySet] = useState(false);

    const initialValues = (setDetails) => {
        if (!manageQuestion) {
            setDetails(prevOption => ({
                ...prevOption,
                style: "text",
                displayTemplate: "Side-by-Side"
            }));
        } else {
            setDetails({
                style: "text",
                displayTemplate: "Side-by-Side"
            })
        }
    }

    useEffect(() => {
        if (!id || !questionId) {
            initialValues(setOptionDetails);
            initialValues(setQuestionDetails);
            initialValues(setAnswerKey);
            initialValues(setHelpText);
        }
    }, [])

    const resetStates = () => {
        setQuestion({
            courseId, sectionId, chapterId
        })
        initialValues(setOptionDetails);
        initialValues(setQuestionDetails);
        initialValues(setAnswerKey);
        initialValues(setHelpText);
        setAnswerLabels([]);
        setValues([]);
        setGroupDetails({});
        setMtfLhs([]);
        setMtfRhs([]);
        setMarks('');
        if (setQuestionId) setQuestionId('');
    }

    useEffect(() => {
        if (id || questionId) {
            loadCurrent({ id: id || questionId });
        }
    }, [id, questionId]);

    // useEffect(() => {
    // loadCourses();
    // loadSection();
    // loadChapter();
    // loadGroup();
    // }, []);

    useEffect(() => {
        if (category) {
            loadCoursesList({ pageSize: 50, category: category });
            setListLoading(false);
        } else {
            loadCoursesList({ pageSize: 50 })
        }
    }, [category, page])

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setPage(0);
    }

    useEffect(() => {
        if (question?.courseId) {
            loadLessonPlan({ id: question?.courseId });
        }
    }, [question?.courseId]);
    useEffect(() => {
        if (question?.courseId) {
            setSectionList(lessonPlan?.sections)
            const selectedSection = lessonPlan?.sections?.find((sec) => sec.sectionId === current?.sectionId || sectionId);
            setChapterList(selectedSection?.chapters)
        }
    }, [lessonPlan]);

    useEffect(() => {
        if (id || questionId) {
            if (loading) return;
            let tempCurrent = {
                id: current?.id,
                status: current?.status,
                courseId: current?.courseId,
                sectionId: current?.sectionId,
                chapterId: current?.chapterId,
                groupDetails: current?.groupDetails,
                type: current?.type,
                category: current?.category,
                taxonomy: current?.taxonomy,
                questionDetails: current?.questionDetails,
                answerDetails: current?.answerDetails
            }
            setQuestion(tempCurrent);
            setQuestionDetails({
                ...questionDetails,
                ...current?.questionDetails
            })
            setGroupDetails(current?.groupDetails)
            setValues(current?.answerDetails?.options?.values)
            setAnswerKey(current?.answerDetails?.options?.answerKey)
            setHelpText(current?.answerDetails?.options?.helpText)
            setAnswerLabels(current?.answerDetails?.options?.answerLabels)
            setMarks(current?.answerDetails?.options?.marks)
            setMtfLhs(current?.answerDetails?.options?.MTFLHS)
            setMtfRhs(current?.answerDetails?.options?.MTFRHS)
        }

    }, [loading, current])

    useEffect(() => {
        if (courseId && sectionId && chapterId) {
            setQuestion(prevQuestion => ({
                ...prevQuestion,
                courseId: courseId,
                sectionId: sectionId,
                chapterId: chapterId
            }));
        }
    }, [courseId, sectionId, chapterId])

    useEffect(() => {
        if ((id || questionId) && current) return;
        if (marksManuallySet) return;
        if (question?.type === 'MCQ') {
            setMarks(1);
        } else if (question?.type === 'MSQ') {
            if (answerLabels?.length) {
                setMarks(answerLabels?.length)
            } else {
                setMarks(1);
            }
        } else if (question?.type === 'MTF') {
            let totalMarks = 0;

            mtfLhs.forEach((item) => {
                totalMarks += item.answerLabels.length; // Each answerLabel counts as 1 mark
            });

            setMarks(totalMarks); // Set the total marks
        } else if (question?.type === 'FIB') {
            if (answerLabels?.length) {
                setMarks(answerLabels?.length)
            } else {
                setMarks(1);
            }
        }

    }, [question, answerLabels, mtfLhs, marksManuallySet])


    const handleSectionChange = (e) => {
        const section = e.target.value
        setQuestion({ ...question, sectionId: section })
        const selectedSection = lessonPlan?.sections?.find((sec) => sec.sectionId === section);
        setChapterList(selectedSection?.chapters)
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "groupDetails") {
            setGroupDetails({
                ...groupDetails, id: value, order: 1
            })
        }
        setQuestion(prevQuestion => ({
            ...prevQuestion,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (!manageQuestion) {
            if (category && category === "Diagnostic") {
                if (question?.courseId && lessonPlan) {
                    const section = lessonPlan?.sections?.[0];
                    const chapter = section?.chapters?.[0];
                    setQuestion(prevQuestion => ({
                        ...prevQuestion,
                        sectionId: section?.sectionId,
                        chapterId: chapter?.chapterId
                    }));
                }
            } else {
                // setQuestion(prevQuestion => ({
                //     ...prevQuestion,
                //     sectionId: '',
                //     chapterId: ''
                // }));
                return
            }
        }
    }, [category, lessonPlan])

    useEffect(() => {

        if (id && allCourseList && current) {
            const course = allCourseList?.find((c) => c.id === current?.courseId);
            setCategory(course ? course?.category : 'Course')
        }
    }, [allCourseList, id, current])

    useEffect(() => {
        if ((!id || !questionId) && !current) {
            if (question?.type === "FIB" || question?.type === "MCQ") {
                setMarks(1)
            } else if (question?.type === "MTF") {
                setMarks(5)
            } else {
                setMarks(2)
            }
        }
    }, [question])

    const handleOption = (e) => {
        const { name, value } = e.target
        if (values.some(option => option.label === value)) {
            toast.warn('Label already exists. Please choose a different label.')
            return;
        }
        if (value.match(/^[A-Za-z1-9]?$/)) {
            setOptionDetails(prevOpt => ({
                ...prevOpt,
                [name]: value,
            }));
        }
    }

    const sortOptions = (options) => {
        return options.sort((a, b) => a.label.localeCompare(b.label));
    };

    const handleAddOption = (optionDetails) => {
        if (values.some(option => option.label === optionDetails?.label)) {
            toast.warn('Label already exists. Please choose a different label.')
            return;
        }
        let temp = {
            label: optionDetails?.label,
            value: {
                style: optionDetails?.style,
                displayTemplate: optionDetails?.displayTemplate,
                text: optionDetails?.text,
                contentURL: optionDetails?.contentURL || ''
            }
        }
        let arr = [...values]
        arr.push(temp)
        setValues(sortOptions(arr))
        setOptionDetails('')
    }

    const handleAnswerLabel = (index) => (e) => {
        const isChecked = e.target.checked;
        let newAnswerLabel = [];
        if (question?.type === "MCQ") {
            if (isChecked) {
                newAnswerLabel = [values[index].label];
            }
        } else {
            newAnswerLabel = [...answerLabels];
            if (isChecked) {
                newAnswerLabel.push(values[index].label);
            } else {
                newAnswerLabel = newAnswerLabel.filter((label) => label !== values[index].label);
            }
        }
        setAnswerLabels(newAnswerLabel);
    }

    const handleMarksChange = (e) => {
        const value = e.target.value;
        if (value === '' || (Number(value) >= 1 && Number(value) <= 10)) {
            setMarks(value)
            setMarksManuallySet(true);
        }
    }

    const isFormValid = () => {
        // Check if questionDetails.text exists
        if (!questionDetails?.text) return false;

        // Check if marks exists
        if (!marks) return false;

        // Check answer options based on question type
        if (question?.type === 'MCQ' || question?.type === 'MSQ') {
            return values?.length >= 2 && values?.some(option => option?.value?.text || option?.value?.contentURL) && answerLabels?.length > 0;
        } else if (question?.type === 'FIB') {
            return answerLabels?.length > 0;
        } else if (question?.type === 'MTF') {
            return mtfLhs?.length >= 2 && mtfRhs?.length >= 2 && mtfLhs?.some(item => item?.value?.text || item?.value?.contentURL) && mtfRhs?.some(item => item?.value?.text || item?.value?.contentURL) && mtfLhs?.some(item => item?.answerLabels?.some(label => label !== null && label !== undefined));
        }

        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let data;
        if (question?.type === "MTF") {
            data = {
                ...question,
                groupDetails,
                questionDetails,
                answerDetails: {
                    options: {
                        MTFLHS: mtfLhs,
                        MTFRHS: mtfRhs,
                        helpText,
                        answerKey,
                        marks,
                        values
                    },
                },
            };
        } else if (question?.type === "FIB") {
            data = {
                ...question,
                groupDetails,
                questionDetails,
                answerDetails: {
                    options: {
                        answerLabels,
                        helpText,
                        answerKey,
                        marks
                    },
                },
            };
        } else {
            data = {
                ...question,
                groupDetails,
                questionDetails,
                answerDetails: {
                    options: {
                        values,
                        answerLabels,
                        helpText,
                        answerKey,
                        marks
                    },
                },
            };
        }
        if (id || questionId) {
            data = { ...data, id: id || questionId }
            if (manageQuestion) {
                save({ question: data });
                resetStates();
                setIsSave(true);
                await loadCourseQuestions({ courseId, sectionId, chapterId })
                if (onQuestionSaved) {
                    onQuestionSaved(data);
                }
            } else {
                save({ question: data, navigate });
            }
        } else {
            if (manageQuestion) {
                create({ question: data });
                resetStates();
                await loadCourseQuestions({ courseId, sectionId, chapterId })
                if (onQuestionSaved) {
                    onQuestionSaved(data);
                }
            } else {
                create({ question: data, navigate });
            }
        }
    };
    const handleModel = () => {
        let data;
        if (question?.type === "MTF") {
            data = {
                ...question,
                groupDetails,
                questionDetails,
                answerDetails: {
                    options: {
                        MTFLHS: mtfLhs,
                        MTFRHS: mtfRhs,
                        helpText,
                        answerKey,
                        marks
                    },
                },
            };
        } else if (question?.type === "FIB") {
            data = {
                ...question,
                groupDetails,
                questionDetails,
                answerDetails: {
                    options: {
                        answerLabels,
                        helpText,
                        answerKey,
                        marks
                    },
                },
            };
        } else {
            data = {
                ...question,
                groupDetails,
                questionDetails,
                answerDetails: {
                    options: {
                        values,
                        answerLabels,
                        helpText,
                        answerKey,
                        marks
                    },
                },
            };
        }
        setQuestion(data)
        setOpenPreview(!openPreview)
    }

    return (
        <Box
            sx={{
                padding: '0px 20px',
                [theme.breakpoints.down('md')]: {
                    padding: '0px',
                },
            }}>
            {!openPreview &&
                <div className='question header-container'>
                    <div className='header'>
                        <Typography
                            align="left"
                            variant="h1"
                            color="primary.blue"
                            sx={{
                                margin: '0',
                                fontWeight: '700',
                                lineHeight: '1.5',
                                fontSize: '2rem',
                            }}>{(id || questionId) ? `Edit Question` : 'Create Question'}</Typography>
                    </div>
                    <form onSubmit={handleSubmit}>
                        {!manageQuestion &&
                            <Grid sx={styles.gridContainer} container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <InputLabel style={styles.inputLabel}>Learning Category<span style={{ color: 'red' }}>*</span></InputLabel>
                                    <FormControl fullWidth>
                                        <Select
                                            name="category"
                                            value={category || ''}
                                            onChange={handleCategoryChange}
                                            placeholder="Course"
                                            sx={{ height: '47px' }}
                                        // disabled={question?.courseId}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {Object.keys(courseConst.CATEGORY).map((key, i) => <MenuItem key={i} value={key}>{courseConst.CATEGORY[key]}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {category === 'Diagnostic' && <>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Diagnostic <span style={{ color: 'red' }}>*</span></InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="courseId"
                                                value={question?.courseId || ''}
                                                onChange={(e) => handleChange(e)}
                                                placeholder="Course"
                                                sx={{ height: '47px' }}
                                                disabled={!category}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {diagnosticList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.id}>{item?.courseName}</MenuItem>
                                                })}
                                                {!lastPage.Diagnostic && (
                                                    <ListSubheader
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <Button
                                                            sx={{
                                                                width: '100%',
                                                                cursor: "pointer",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: 'space-between'
                                                            }}
                                                            onClick={() => {
                                                                setListLoading(true);
                                                                setTimeout(() => setPage(prev => prev + 1), 0);
                                                            }} placeholder={listLoading ? <CircularProgress /> : 'Load More'} endIcon={!listLoading && <CaretDown />} />
                                                    </ListSubheader>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>}
                                {category === "Course" && <>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Course <span style={{ color: 'red' }}>*</span></InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="courseId"
                                                value={question?.courseId || ''}
                                                onChange={(e) => handleChange(e)}
                                                placeholder="Course"
                                                sx={{ height: '47px' }}
                                                disabled={!category}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {courseList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.id}>{item?.courseName}</MenuItem>
                                                })}
                                                {!lastPage.Course && (
                                                    <ListSubheader
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <Button
                                                            sx={{
                                                                width: '100%',
                                                                cursor: "pointer",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: 'space-between'
                                                            }}
                                                            onClick={() => {
                                                                setListLoading(true);
                                                                setTimeout(() => setPage(prev => prev + 1), 0);
                                                            }} placeholder={listLoading ? <CircularProgress /> : 'Load More'} endIcon={!listLoading && <CaretDown />} />
                                                    </ListSubheader>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Chapter <span style={{ color: 'red' }}>*</span></InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="sectionId"
                                                value={question?.sectionId || ''}
                                                onChange={(e) => handleSectionChange(e)}
                                                placeholder="Section"
                                                sx={{ height: '47px' }}
                                                disabled={!question?.courseId}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {sectionList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.sectionId}>{item?.name}</MenuItem>
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Session <span style={{ color: 'red' }}>*</span></InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="chapterId"
                                                value={question?.chapterId || ''}
                                                onChange={(e) => handleChange(e)}
                                                placeholder="Chapter"
                                                sx={{ height: '47px' }}
                                                disabled={!question?.sectionId}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {chapterList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.chapterId}>{item?.name}</MenuItem>
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>}
                            </Grid>}

                        <Grid sx={styles.gridContainer} container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel style={styles.inputLabel}>Type <span style={{ color: 'red' }}>*</span></InputLabel>
                                <FormControl fullWidth>
                                    <Select
                                        name="type"
                                        value={question?.type || ''}
                                        onChange={(e) => handleChange(e)}
                                        placeholder="Type"
                                        sx={{ height: '47px' }}
                                        disabled={!question?.chapterId || questionDetails?.text}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {questionConstant.QUESTION_TYPES.map((item, index) => {
                                            return <MenuItem key={index} value={item}>{item}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel style={styles.inputLabel}>Category (Optional)</InputLabel>
                                <FormControl fullWidth>
                                    <Select
                                        name="category"
                                        value={question?.category || ''}
                                        onChange={(e) => handleChange(e)}
                                        placeholder="Category"
                                        sx={{ height: '47px' }}
                                        disabled={!question?.type}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {questionConstant.CATEGORIES.map((item, index) => {
                                            return <MenuItem key={index} value={item}>{item}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel style={styles.inputLabel}>Taxonomy (Optional)</InputLabel>
                                <FormControl fullWidth>
                                    <Select
                                        name="taxonomy"
                                        value={question?.taxonomy || ''}
                                        onChange={(e) => handleChange(e)}
                                        placeholder="Taxonomy"
                                        sx={{ height: '47px' }}
                                        disabled={!question?.category}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {questionConstant.TAXONAMY.map((item, index) => {
                                            return <MenuItem key={index} value={item}>{item}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {!manageQuestion && <Grid sx={styles.gridContainer} container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel style={styles.inputLabel}>Case Study (Optional)</InputLabel>
                                <FormControl fullWidth>
                                    <Select
                                        name="groupDetails"
                                        value={groupDetails?.id || ''}
                                        onChange={(e) => handleChange(e)}
                                        placeholder="Group"
                                        sx={{ height: '47px' }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {groupList?.data?.map((item, index) => {
                                            return <MenuItem key={index} value={item?.id}>{item.groupName}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>}

                        <Grid sx={styles.gridContainer} container spacing={2} alignItems="center">
                            {question?.type && <>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography
                                        align="left"
                                        variant="h6"
                                        color="primary.blue"
                                        x sx={{
                                            margin: '0',
                                            fontWeight: '700',
                                            lineHeight: '1.5',
                                        }}>Questions <span style={{ color: 'red' }}>*</span></Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Layout
                                        type={question?.type === 'FIB' ? 'FIB' : ''}
                                        details={questionDetails}
                                        setDetails={setQuestionDetails}
                                        question={true}
                                    />
                                </Grid>
                            </>}

                            {(question?.type === 'MCQ' || question?.type === 'MSQ') && <>
                                {(questionDetails?.text) && <>
                                    <LayoutMcqMsq
                                        values={values}
                                        setValues={setValues}
                                        optionDetails={optionDetails}
                                        setOptionDetails={setOptionDetails}
                                        handleAddOption={handleAddOption}
                                        sortOptions={sortOptions}
                                        handleOption={handleOption}
                                        question={question}
                                        answerLabels={answerLabels}
                                        handleAnswerLabel={handleAnswerLabel}
                                        setAnswerLabels={setAnswerLabels}
                                    />
                                </>}
                            </>
                            }
                            {(question?.type === 'FIB') && <>
                                {(questionDetails?.text) && <>
                                    <LayoutFill
                                        details={questionDetails}
                                        answerLabels={answerLabels}
                                        setAnswerLabels={setAnswerLabels}
                                    />
                                </>}
                            </>
                            }
                            {(question?.type === 'MTF') && <>
                                {(questionDetails?.text) && <>
                                    <LayoutMtf
                                        MTFLHS={mtfLhs}
                                        MTFRHS={mtfRhs}
                                        setMTFLHS={setMtfLhs}
                                        setMTFRHS={setMtfRhs}
                                        sortOptions={sortOptions}
                                        values={values}
                                        setValues={setValues}
                                    />

                                </>}
                            </>
                            }

                            {(answerLabels?.length > 0 || (question?.type === "FIB" && values?.length > 0) || (question?.type === "MTF" && mtfLhs?.length > 0)) && <>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography
                                        align="left"
                                        variant="h6"
                                        color="primary.blue"
                                        sx={{
                                            margin: '0',
                                            fontWeight: '700',
                                            lineHeight: '1.5',
                                        }}>Answer Key (Optional)</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Layout
                                        details={answerKey}
                                        setDetails={setAnswerKey}
                                        answerKey={true}
                                    />
                                </Grid>
                            </>}

                            {(answerLabels?.length > 0 || (question?.type === "FIB" && values?.length > 0) || (question?.type === "MTF" && mtfLhs?.length > 0)) && <>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography
                                        align="left"
                                        variant="h6"
                                        color="primary.blue"
                                        sx={{
                                            margin: '0',
                                            fontWeight: '700',
                                            lineHeight: '1.5',
                                        }}>Help Text (Optional)</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Layout
                                        details={helpText}
                                        setDetails={setHelpText}
                                        helpText={true}
                                    />
                                </Grid>
                            </>}
                        </Grid>

                        {(answerLabels?.length > 0 || (question?.type === "FIB" && values?.length > 0) || (question?.type === "MTF" && mtfLhs?.length > 0)) && <Grid sx={styles.gridContainer} container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={4} lg={3} >
                                <InputLabel style={styles.inputLabel}>Marks <span style={{ color: 'red' }}>*</span></InputLabel>
                                <TextField
                                    name="marks"
                                    fullWidth
                                    value={marks || ''}
                                    onChange={(e) => handleMarksChange(e)}
                                    placeholder="Enter Mark"
                                    required
                                    type="number"
                                    inputProps={{
                                        pattern: "[1-10]",
                                        style: {
                                            '-moz-appearance': 'textfield',
                                            'appearance': 'textfield',
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment sx={{ cursor: "pointer" }} position="end">
                                                <Tooltip title="Provide the desired mark allocation for the question type in numeric (i.e 1- 10 )">
                                                    <Info />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                '-webkit-appearance': 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                '-webkit-appearance': 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]': {
                                                '-moz-appearance': 'textfield',
                                                'appearance': 'textfield',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        }
                        <Box sx={{ mt: 4, display: 'flex', gap: '10px' }}>
                            <Button size='large' variant="contained" disabled={!isFormValid()} onClick={handleModel} color="primary" placeholder={'Preview'} />

                            <Button size='large' type="submit" variant="contained" disabled={!isFormValid()} color="primary" placeholder={(!id && !questionId) ? 'Create' : 'Save Changes'} />
                        </Box>
                    </form>

                </div>}
            {openPreview && !manageQuestion &&
                <PreviewQuestion openPreview={openPreview} question={question} handleClose={handleModel} />
            }
            {manageQuestion &&
                <Modal open={openPreview} >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '80%',
                            height: '80%',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            borderRadius: '10px',
                            p: 4,
                            overflow: 'auto',
                        }}
                    >
                        <IconButton
                            onClick={handleModel}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                            }}
                        >
                            <X />
                        </IconButton>
                        <Preview question={question} />
                        {/* Add more details about the question if needed */}
                    </Box>
                </Modal>
            }
        </Box >
    );
};

const styles = {
    label: {
        color: 'var(--mui-palette-common-black)',
        fontWeight: 'bold'
    },
    inputLabel: {
        color: 'var(--mui-palette-primary-darkGrey)',
        marginBottom: '5px',
        fontWeight: '600',
        fontSize: '14px'

    },
    gridContainer: {
        mt: 2,
    },
    suggestionBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '10px',
    },
    suggestionItem: {
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '2px',
        cursor: 'pointer',
        boxShadow: '0 4px 8px var(--mui-palette-secondary-boxShadow1)',
        backgroundColor: 'var(--mui-palette-gray-lightGrey300)',
    },
    uploadBtn: {
        width: '50px',
        height: '50px',
        borderRadius: '5px',
    },
    hiddenInput: {
        display: 'none',
    }
};

export default Edit;